import type { Report } from './types'
import { cssSelector, domPath, xPath } from './selector'
import { computedStyles, matchedRules } from './css'

// Always-redacted, regardless of config: passwords and fields the browser tags
// as sensitive (credit card, one-time codes). Hosts add more via config.redact.
const ALWAYS_REDACT = [
  'input[type="password"]',
  'input[autocomplete*="cc-number"]',
  'input[autocomplete="cc-csc"]',
  'input[autocomplete="one-time-code"]',
  'input[name*="password" i]',
  'input[name*="cardnumber" i]',
  '[data-qc-redact]',
].join(', ')
const TEXT_CAP = 2000
const EXCERPT_CAP = 16384
const ERROR_RING_SIZE = 10

// ---- console/error ring buffer (installed once at init; listener-based, no monkey-patching) ----

const errorRing: string[] = []
let ringInstalled = false

export function installErrorRing(): void {
  if (ringInstalled) return
  ringInstalled = true
  window.addEventListener('error', (e) => {
    pushError(e.message ? `${e.message} (${e.filename ?? '?'}:${e.lineno ?? '?'})` : String(e))
  })
  window.addEventListener('unhandledrejection', (e) => {
    pushError(`Unhandled rejection: ${truncate(String(e.reason), 300)}`)
  })
}

function pushError(msg: string): void {
  errorRing.push(truncate(msg, 500))
  if (errorRing.length > ERROR_RING_SIZE) errorRing.shift()
}

export function recentErrors(): string[] {
  return [...errorRing]
}

// ---- capture helpers ----

function truncate(s: string, cap: number): string {
  return s.length > cap ? s.slice(0, cap - 1) + '…' : s
}

function redactedClone(el: Element, extraSelectors: string[]): Element {
  const clone = el.cloneNode(true) as Element
  const selector = [ALWAYS_REDACT, ...extraSelectors].join(', ')
  const targets = clone.matches?.(selector) ? [clone] : []
  clone.querySelectorAll?.(selector).forEach((n) => targets.push(n))
  for (const t of targets) {
    t.removeAttribute('value')
    if (t.childNodes.length) t.textContent = '•••'
    t.setAttribute('data-qc-redacted', '')
  }
  return clone
}

function pickAttributes(el: Element): Record<string, string> {
  const out: Record<string, string> = {}
  for (const attr of Array.from(el.attributes)) {
    if (attr.name === 'value' || attr.name.startsWith('on')) continue
    out[attr.name] = truncate(attr.value, 300)
    if (Object.keys(out).length >= 20) break
  }
  return out
}

function nearestQcId(el: Element): string | undefined {
  const owner = el.closest('[data-qc-id]')
  return owner?.getAttribute('data-qc-id') ?? undefined
}

export function buildTarget(el: Element, redact: string[], captureStyles = true): Report['target'] {
  const rect = el.getBoundingClientRect()
  const text = (el as HTMLElement).innerText ?? el.textContent ?? ''
  const excerptSource = redactedClone(el, redact)
  const target: Report['target'] = {
    kind: 'element',
    url: location.href,
    route: location.pathname,
    css_selector: cssSelector(el),
    dom_path: domPath(el),
    xpath: xPath(el),
    text_content: truncate(text.trim(), TEXT_CAP),
    outer_html_excerpt: truncate(excerptSource.outerHTML, EXCERPT_CAP),
    bounding_box: {
      x: Math.round(rect.x),
      y: Math.round(rect.y),
      w: Math.round(rect.width),
      h: Math.round(rect.height),
    },
    attributes: pickAttributes(el),
  }
  if (captureStyles) {
    target.computed_styles = computedStyles(el)
    const { rules, unreadable } = matchedRules(el)
    target.matched_rules = rules
    if (unreadable.length > 0) {
      // Stash for async recovery at submit time (fetch cross-origin sheets).
      target._unreadable_sheets = unreadable
      target.styles_note = `${unreadable.length} cross-origin stylesheet(s) not readable via CSSOM`
    }
  }
  return target
}

export function buildSourceHints(el: Element): Report['source_hints'] {
  const hints: NonNullable<Report['source_hints']> = { framework: 'unknown' }
  const qcId = nearestQcId(el)
  if (qcId) hints.data_qc_id = qcId
  return hints
}

export function buildEnvironment(): Report['environment'] {
  return {
    viewport: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio || 1 },
    user_agent: navigator.userAgent,
    console_errors: recentErrors(),
  }
}
